import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { incrementGuestCount, decrementGuestCount } from './userRecordsService';

export interface Guest {
  id?: string;
  name: string;
  lastName?: string;
  email?: string;
  phone?: string;
  zipCode?: string; // CEP
  address?: string;
  number?: string; // Número
  complement?: string; // Complemento/Apto
  neighborhood?: string; // Bairro
  city?: string; // Cidade
  state?: string; // Estado
  birthday?: string;
  location?: string; // Grupo
  status: 'pending' | 'confirmed' | 'declined';
  dietaryRestrictions?: string[];
  allergies?: string;
  drinks?: string[];
  preferences?: string[];
  notes?: string;
  maritalStatus?: 'single' | 'married' | 'divorced' | 'widowed' | 'other'; // Estado civil
  hasChildren?: boolean; // Tem filhos
  childrenCount?: number; // Número de filhos
  eventId?: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

export const createGuest = async (userId: string, guestData: Omit<Guest, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const guestsRef = collection(db, 'guests');

    // Remover campos undefined
    const cleanedData = Object.entries(guestData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    const newGuest = {
      ...cleanedData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(guestsRef, newGuest);
    await incrementGuestCount(userId);

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar convidado:', error);
    throw error;
  }
};

export const getUserGuests = async (userId: string): Promise<Guest[]> => {
  try {
    const guestsRef = collection(db, 'guests');
    const q = query(
      guestsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const guests: Guest[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      guests.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Guest);
    });

    return guests;
  } catch (error) {
    console.error('Erro ao buscar convidados:', error);
    throw error;
  }
};

// Alias para manter compatibilidade
export const getGuests = getUserGuests;

export const updateGuest = async (guestId: string, updates: Partial<Guest>): Promise<void> => {
  try {
    // Um campo presente no update mas vazio (undefined/''/null) significa que o
    // usuário LIMPOU aquele campo na edição — grava deleteField() para de fato
    // apagá-lo no Firestore, em vez de silenciosamente manter o valor antigo.
    // Campos que o chamador não incluiu ficam intactos (não entram no reduce).
    const cleanedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
      acc[key] = (value === undefined || value === '' || value === null) ? deleteField() : value;
      return acc;
    }, {} as Record<string, any>);

    const guestRef = doc(db, 'guests', guestId);
    await updateDoc(guestRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao atualizar convidado:', error);
    throw error;
  }
};

export const deleteGuest = async (guestId: string, userId: string): Promise<void> => {
  try {
    const guestRef = doc(db, 'guests', guestId);
    await deleteDoc(guestRef);
    await decrementGuestCount(userId);
  } catch (error) {
    console.error('Erro ao deletar convidado:', error);
    throw error;
  }
};

export const getGuest = async (guestId: string): Promise<Guest | null> => {
  try {
    const guestRef = doc(db, 'guests', guestId);
    const guestDoc = await getDoc(guestRef);

    if (guestDoc.exists()) {
      const data = guestDoc.data();
      return {
        id: guestDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Guest;
    }

    return null;
  } catch (error) {
    console.error('Erro ao buscar convidado:', error);
    throw error;
  }
};